import React, { useId, useState } from 'react';
import { isMaskedSecret } from '@/utils/maskedSecret';

export type SecretInputProps = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  autoComplete?: string;
  required?: boolean;
  /** When true, show a copy button for non-empty, non-masked values (admin/owner only). */
  allowCopy?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
};

function isCopyableValue(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && !isMaskedSecret(trimmed);
}

export default function SecretInput({
  id: idProp,
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  autoComplete,
  required,
  allowCopy = false,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
}: SecretInputProps) {
  const autoId = useId();
  const inputId = idProp ?? autoId;
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const showCopy = allowCopy && isCopyableValue(value);

  async function handleCopy() {
    if (!showCopy) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable */
    }
  }

  return (
    <div className="secret-input">
      <input
        id={inputId}
        className={className}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        required={required}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
      />
      <div className="secret-input__actions">
        {showCopy && (
          <button
            type="button"
            className="secret-input__action btn btn-ghost btn-sm"
            onClick={() => void handleCopy()}
            aria-label={copied ? 'Copied' : 'Copy value'}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
        <button
          type="button"
          className="secret-input__action btn btn-ghost btn-sm"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide characters' : 'Show characters'}
          aria-pressed={visible}
          disabled={disabled}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}
